/**
 * Preferencias de notificaciones por usuario (Fase B + forced channels de Fase D).
 *
 * Los canales marcados como "obligatorios" por el admin aparecen bloqueados
 * (candado) y el usuario no puede desactivarlos.
 */
import { useEffect, useMemo, useState } from 'react'
import { Bell, Mail, Loader2, Lock, HelpCircle } from 'lucide-react'
import { Button } from '@/components/Button'
import { ToastContainer, useToast } from '@/components/Toast'
import { TableSkeleton } from '@/components/Skeleton'
import { HelpPanel } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  type NotificationChannel,
  type NotificationPreference,
} from '@/modules/notifications/hooks/useNotifications'

const CHANNELS: { key: NotificationChannel; label: string; icon: typeof Bell }[] = [
  { key: 'in_app', label: 'En la app', icon: Bell },
  { key: 'email', label: 'Email', icon: Mail },
]

const MODULE_LABELS: Record<string, string> = {
  'sales.documents': 'Ventas — Documentos',
  'sales.orders': 'Ventas — Pedidos',
  'sales.subscriptions': 'Ventas — Suscripciones',
  'sales.collections': 'Ventas — Cobranza',
  'purchases.invoices': 'Compras — Facturas',
  'receivables.collections': 'Cobranza',
  'receivables.reminders': 'Cobranza — Recordatorios',
  'payables': 'Cuentas por Pagar',
  'inventory': 'Inventario',
  'inventory.transfers': 'Inventario — Transferencias',
  'payroll.payslips': 'Nómina — Liquidaciones',
  'payroll.contracts': 'Nómina — Contratos',
  'config': 'Configuración',
}

type DraftMap = Record<string, NotificationChannel[]>

function toDraft(prefs: NotificationPreference[]): DraftMap {
  return Object.fromEntries(prefs.map(p => [p.event_type, [...p.channels]]))
}

export function NotificationPreferencesTab() {
  const { data, isLoading } = useNotificationPreferences()
  const { mutate: update, isPending } = useUpdateNotificationPreferences()
  const toast = useToast()
  const [draft, setDraft] = useState<DraftMap>({})
  const [helpOpen, setHelpOpen] = useState(false)
  const helpContent = HELP['profile.notifications']

  useEffect(() => {
    if (data) setDraft(toDraft(data))
  }, [data])

  const grouped = useMemo(() => {
    if (!data) return []
    const map = new Map<string, NotificationPreference[]>()
    for (const p of data) {
      const arr = map.get(p.module) || []
      arr.push(p)
      map.set(p.module, arr)
    }
    return Array.from(map.entries()).map(([module, events]) => ({ module, events }))
  }, [data])

  const dirty = useMemo(() => {
    if (!data) return false
    return data.some(p => {
      const d = draft[p.event_type] || []
      return d.length !== p.channels.length || d.some(c => !p.channels.includes(c))
    })
  }, [data, draft])

  const toggle = (eventType: string, channel: NotificationChannel, forced: NotificationChannel[]) => {
    if (forced.includes(channel)) return
    setDraft(prev => {
      const current = prev[eventType] || []
      return {
        ...prev,
        [eventType]: current.includes(channel)
          ? current.filter(c => c !== channel)
          : [...current, channel],
      }
    })
  }

  const onSave = () => {
    const payload = Object.entries(draft).map(([event_type, channels]) => ({ event_type, channels }))
    update(payload, {
      onSuccess: () => toast.success('Preferencias guardadas'),
      onError: () => toast.error('No se pudieron guardar las preferencias'),
    })
  }

  const onReset = () => {
    if (data) setDraft(toDraft(data))
  }

  if (isLoading) return <TableSkeleton rows={8} cols={4} />

  return (
    <div className="space-y-4">
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 flex items-start gap-3 shadow-card">
        <div className="flex-1">
          <p className="text-sm text-content-muted">
            Elige por qué canales recibir cada tipo de aviso. Los canales con candado
            son obligatorios por el administrador y no pueden desactivarse.
          </p>
        </div>
        {helpContent && (
          <button
            onClick={() => setHelpOpen(true)}
            className="p-1 rounded-full text-content-subtle hover:text-primary hover:bg-primary-50 transition-colors"
            title="Más información"
            aria-label="Más información sobre notificaciones"
          >
            <HelpCircle size={18} />
          </button>
        )}
      </div>

      {helpContent && (
        <HelpPanel
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          title={helpContent.title}
          sections={helpContent.sections}
        />
      )}

      {grouped.map(({ module, events }) => (
        <div key={module} className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
          <div className="px-5 py-3 border-b border-divider bg-surface-muted">
            <h3 className="text-sm font-semibold text-content">{MODULE_LABELS[module] || module}</h3>
          </div>
          <div className="divide-y divide-divider">
            {events.map(ev => {
              const enabled = draft[ev.event_type] || []
              const forced = ev.forced_channels
              return (
                <div key={ev.event_type} className="flex items-center gap-4 px-5 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-content truncate">{ev.description}</p>
                    <p className="text-xs text-content-subtle">{ev.event_type}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {CHANNELS.map(({ key, label, icon: Icon }) => {
                      const isForced = forced.includes(key)
                      const isActive = isForced || enabled.includes(key)
                      return (
                        <label
                          key={key}
                          className={`flex items-center gap-1.5 select-none ${
                            isForced ? 'cursor-not-allowed' : 'cursor-pointer'
                          } ${isActive ? 'text-primary' : 'text-content-subtle'}`}
                          title={isForced ? `${label} · Obligatorio por el administrador` : label}
                        >
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={isActive}
                              disabled={isForced}
                              onChange={() => toggle(ev.event_type, key, forced)}
                              className="w-4 h-4 rounded border-divider-strong text-primary focus:ring-primary/30 disabled:opacity-70"
                              aria-label={`${label} para ${ev.description}${isForced ? ' (obligatorio)' : ''}`}
                            />
                            {isForced && (
                              <Lock size={8} className="absolute -top-1 -right-1.5 text-warning-content-soft" />
                            )}
                          </div>
                          <Icon size={14} />
                        </label>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      <div className="sticky bottom-0 bg-surface border-t border-divider p-4 flex justify-end gap-2 rounded-b-2xl">
        <Button variant="ghost" onClick={onReset} disabled={!dirty || isPending}>
          Descartar
        </Button>
        <Button
          variant="primary"
          onClick={onSave}
          disabled={!dirty || isPending}
          leftIcon={isPending ? <Loader2 size={14} className="animate-spin" /> : undefined}
        >
          Guardar cambios
        </Button>
      </div>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
